import { DestroyRef, Directive, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSelect } from '@angular/material/select';
import { filter } from 'rxjs/operators';

/**
 * Material sizes a select panel to its form field by default, so option names longer than the
 * field are clipped. This lets the panel grow to its content (capped in the global styles) while
 * staying at least as wide as the field, and shows a cut-off selected value in full on hover.
 */
@Directive({
  selector: 'mat-select',
  standalone: true,
})
export class SelectPanelWidthDirective implements OnInit {
  private readonly select = inject(MatSelect);
  private readonly host: HTMLElement = inject(ElementRef).nativeElement;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    if (this.select.panelWidth !== 'auto') return;
    this.select.panelWidth = null;

    /* Emitted in a microtask after the panel is attached but before the overlay is positioned,
       so the minimum width is part of the size the overlay positions against the viewport. */
    this.select.openedChange
      .pipe(filter(Boolean), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.matchFieldWidth());
  }

  @HostListener('mouseenter')
  showFullValueWhenTruncated(): void {
    /* The ellipsis is drawn by the block-level value container; its inline text span reports no width. */
    const value = this.host.querySelector<HTMLElement>('.mat-mdc-select-value');
    const truncated = !!value && value.scrollWidth > value.clientWidth;
    if (truncated) {
      this.host.title = value.textContent?.trim() ?? '';
    } else {
      this.host.removeAttribute('title');
    }
  }

  private matchFieldWidth(): void {
    const panel = document.getElementById(`${this.select.id}-panel`);
    if (!panel) return;
    const field = this.host.closest<HTMLElement>('.mat-mdc-text-field-wrapper') ?? this.host;
    panel.style.minWidth = `${field.getBoundingClientRect().width}px`;
  }
}
